import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useComplianceAnalysis } from '../../../hooks/useComplianceAnalysis';

const SimilarContentComparison = ({ currentContent, onCompareSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [comparisonResults, setComparisonResults] = useState(null);
  const [isComparing, setIsComparing] = useState(false);

  const { analyzeSimilar } = useComplianceAnalysis();

  const similarContent = [
    {
      id: 'content-001',
      title: 'Q3 Savings Account Promotion',
      platform: 'Facebook',
      status: 'Approved',
      complianceScore: 95,
      author: 'Sarah Johnson',
      createdDate: '2024-09-18',
      text: `Boost your savings with our new high-yield savings account! \n\nEarn up to 4.25% APY* on your deposits. FDIC insured up to $250,000 per depositor. \n\n*Annual Percentage Yield. Rate subject to change. Minimum balance requirements apply. Member FDIC.`,
      similarities: [
        'Similar promotional language for savings products',
        'Includes required FDIC disclosures',
        'Uses compliant APY terminology'
      ],
      differences: [
        'Different interest rate mentioned',
        'More detailed disclaimer language',
        'Includes minimum balance disclosure'
      ]
    },
    {
      id: 'content-002',
      title: 'Credit Card Cashback Campaign',
      platform: 'Instagram',
      status: 'Rejected',
      complianceScore: 65,
      author: 'Mike Chen',
      createdDate: '2024-09-16',
      text: `Get cash back on every purchase! \n\nUp to 5% back on dining and entertainment. No annual fee for the first year! \n\nApply now and start earning rewards immediately.`,
      similarities: [
        'Promotional tone for financial products',
        'Mentions specific percentage benefits',
        'Call-to-action for immediate application'
      ],
      differences: [
        'Missing required credit card disclosures',
        'No APR information provided',
        'Lacks terms and conditions reference'
      ],
      rejectionReasons: [
        'Missing required APR disclosure',
        'No mention of credit approval requirements',
        'Insufficient terms and conditions information'
      ]
    },
    {
      id: 'content-003',
      title: 'Personal Loan Summer Special',
      platform: 'LinkedIn',
      status: 'Changes Requested',
      complianceScore: 78,
      author: 'Jennifer Davis',
      createdDate: '2024-09-14',
      text: `Need funds for your summer plans? \n\nPersonal loans starting at 6.99% APR for qualified borrowers. Quick approval process with funds available in 24 hours. \n\nTerms and conditions apply. Visit our website for complete details.`,
      similarities: [
        'Includes APR disclosure for loan products',
        'References terms and conditions',
        'Mentions qualification requirements'
      ],
      differences: [
        'Different loan product type',
        'More conservative promotional language',
        'Includes processing time information'
      ],
      requestedChanges: [
        'Add representative APR range',
        'Include loan amount limits',
        'Clarify qualification criteria'
      ]
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Approved': 'text-green-600 bg-green-50 border-green-200',
      'Rejected': 'text-red-600 bg-red-50 border-red-200',
      'Changes Requested': 'text-orange-600 bg-orange-50 border-orange-200',
      'Pending': 'text-yellow-600 bg-yellow-50 border-yellow-200'
    };
    return colors?.[status] || 'text-gray-600 bg-gray-50 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Approved': 'CheckCircle',
      'Rejected': 'XCircle',
      'Changes Requested': 'AlertCircle',
      'Pending': 'Clock'
    };
    return icons?.[status] || 'Clock';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleCompareSelect = (content) => {
    if (onCompareSelect) {
      onCompareSelect(content);
    }
  };

  const getPlatformIcon = (platform) => {
    const platformIcons = {
      'Facebook': 'Facebook',
      'Twitter': 'Twitter',
      'LinkedIn': 'Linkedin',
      'Instagram': 'Instagram',
      'Website': 'Globe'
    };
    return platformIcons?.[platform] || 'Globe';
  };

  const getRiskBadgeColor = (riskLevel) => {
    const colors = {
      'Low': 'bg-green-100 text-green-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-red-100 text-red-800'
    };
    return colors?.[riskLevel] || 'bg-gray-100 text-gray-800';
  };

  const handleAIComparison = async () => {
    if (!currentContent?.text && !currentContent?.preview) return;

    setIsComparing(true);
    
    try {
      const contentText = currentContent?.text || currentContent?.preview;
      const results = await analyzeSimilar(contentText, similarContent);
      
      if (results) {
        setComparisonResults(results);
      }
    } catch (error) {
      console.error('AI comparison error:', error);
    } finally {
      setIsComparing(false);
    }
  };

  // Add missing filteredContent logic
  const filteredContent = similarContent.filter(content => {
    const matchesSearch = !searchTerm || 
      content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      content.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      content.author.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPlatform = selectedPlatform === 'all' || content.platform === selectedPlatform;
    const matchesStatus = selectedStatus === 'all' || content.status === selectedStatus;
    
    return matchesSearch && matchesPlatform && matchesStatus;
  });

  return (
    <div className="h-full flex flex-col">
      {/* Enhanced Header */}
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
          <Icon name="GitCompare" size={20} />
          <span>Similar Content Analysis</span>
          <Icon name="Brain" size={16} className="text-purple-600" />
        </h3>
        
        <div className="space-y-3">
          <Button
            onClick={handleAIComparison}
            disabled={isComparing || (!currentContent?.text && !currentContent?.preview)}
            className="w-full bg-purple-600 hover:bg-purple-700"
            iconName={isComparing ? "Loader2" : "Sparkles"}
            iconClassName={isComparing ? "animate-spin" : ""}
          >
            {isComparing ? 'Analyzing with AI...' : 'AI-Powered Content Analysis'}
          </Button>

          {/* Search and Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                Search Content
              </label>
              <input
                type="text"
                placeholder="Search by title, text, or author..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full p-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                Platform Filter
              </label>
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e?.target?.value)}
                className="w-full p-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Platforms</option>
                <option value="Facebook">Facebook</option>
                <option value="Instagram">Instagram</option>
                <option value="LinkedIn">LinkedIn</option>
                <option value="Twitter">Twitter</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1 block">
                Status Filter
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e?.target?.value)}
                className="w-full p-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Statuses</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Changes Requested">Changes Requested</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      {/* AI Analysis Results */}
      {comparisonResults && (
        <div className="p-4 border-b border-border bg-purple-50">
          <h4 className="font-medium text-purple-800 mb-3 flex items-center space-x-2">
            <Icon name="Brain" size={16} />
            <span>AI Comparative Analysis</span>
          </h4>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white p-3 rounded-lg border border-purple-200">
              <div className="text-sm font-medium text-purple-800">Similarity Score</div>
              <div className="text-lg font-bold text-purple-900">
                {Math.round(comparisonResults?.similarityScore || 0)}%
              </div>
            </div>
            <div className="bg-white p-3 rounded-lg border border-purple-200">
              <div className="text-sm font-medium text-purple-800">Risk Level</div>
              <div className="text-lg font-bold text-purple-900">
                {comparisonResults?.riskAssessment?.split(' ')?.[0] || 'Unknown'}
              </div>
            </div>
          </div>

          {/* Common Patterns */}
          {comparisonResults?.commonPatterns?.length > 0 && (
            <div className="mb-4">
              <h5 className="font-medium text-sm mb-2">Common Compliance Patterns</h5>
              <div className="space-y-1">
                {comparisonResults?.commonPatterns?.slice(0, 3)?.map((pattern, index) => (
                  <div key={index} className="text-sm bg-white p-2 rounded border border-purple-200">
                    <Icon name="TrendingUp" size={14} className="inline mr-2 text-purple-600" />
                    {pattern}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Success Factors */}
          {comparisonResults?.successFactors?.length > 0 && (
            <div className="mb-4">
              <h5 className="font-medium text-sm mb-2">Success Factors from High-Scoring Content</h5>
              <div className="space-y-1">
                {comparisonResults?.successFactors?.slice(0, 3)?.map((factor, index) => (
                  <div key={index} className="text-sm bg-white p-2 rounded border border-green-200">
                    <Icon name="CheckCircle" size={14} className="inline mr-2 text-green-600" />
                    {factor}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {comparisonResults?.recommendations?.length > 0 && (
            <div>
              <h5 className="font-medium text-sm mb-2">AI Recommendations</h5>
              <div className="space-y-1">
                {comparisonResults?.recommendations?.slice(0, 3)?.map((recommendation, index) => (
                  <div key={index} className="text-sm bg-white p-2 rounded border border-blue-200">
                    <Icon name="Lightbulb" size={14} className="inline mr-2 text-blue-600" />
                    {recommendation}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex-1 overflow-y-auto">
        {filteredContent?.map((content) => (
          <div
            key={content?.id}
            className="border-b border-border last:border-b-0 hover:bg-muted/50 transition-colors"
          >
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-sm">{content?.platform}</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskBadgeColor(content?.riskLevel)}`}>
                      {content?.riskLevel}
                    </span>
                    {comparisonResults && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                        AI Analyzed
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                    {content?.preview}
                  </p>
                </div>
                
                <div className="ml-4 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    iconName="GitCompare"
                    onClick={() => onCompareSelect(content)}
                  >
                    Compare
                  </Button>
                  <div className="text-xs text-muted-foreground mt-1">
                    Score: {content?.complianceScore}/100
                  </div>
                </div>
              </div>

              {/* Metrics Display */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="bg-muted/30 border border-border rounded-md p-3">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Compliance Score</div>
                  <div className="text-lg font-bold text-foreground">{content?.complianceScore}/100</div>
                </div>
                <div className="bg-muted/30 border border-border rounded-md p-3">
                  <div className="text-sm font-medium text-muted-foreground mb-1">Status</div>
                  <div className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusColor(content?.status)}`}>
                    <Icon 
                      name={getStatusIcon(content?.status)} 
                      size={12} 
                      className="inline mr-1" 
                    />
                    {content?.status}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Similarities</h4>
                  <ul className="space-y-1">
                    {content?.similarities?.map((similarity, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm text-muted-foreground">
                        <Icon name="Check" size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                        <span>{similarity}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Key Differences</h4>
                  <ul className="space-y-1">
                    {content?.differences?.map((difference, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm text-muted-foreground">
                        <Icon name="AlertCircle" size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{difference}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {content?.status === 'Rejected' && content?.rejectionReasons && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-foreground mb-2">Rejection Reasons</h4>
                  <ul className="space-y-1">
                    {content?.rejectionReasons?.map((reason, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm text-red-600">
                        <Icon name="X" size={14} className="mt-0.5 flex-shrink-0" />
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {content?.status === 'Changes Requested' && content?.requestedChanges && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-foreground mb-2">Requested Changes</h4>
                  <ul className="space-y-1">
                    {content?.requestedChanges?.map((change, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm text-orange-600">
                        <Icon name="Edit" size={14} className="mt-0.5 flex-shrink-0" />
                        <span>{change}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="GitCompare"
                  iconPosition="left"
                  onClick={() => handleCompareSelect(content)}
                >
                  Detailed Compare
                </Button>
              </div>
            </div>
          </div>
        ))}

        {filteredContent?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Similar Content Found</h3>
            <p className="text-muted-foreground">No previously reviewed content matches the current analysis criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimilarContentComparison;